import type { CashflowTargetGranularity } from '@moment/horizon-data-access/proposal'

import { CashflowFrequency, CashflowFrequencyMap } from './types'

type RecurringFrequency = Exclude<`${CashflowFrequency}`, 'one_time'>

type GranularityIssue =
  | 'granularity_on_one_time'
  | 'granularity_wider_than_frequency'

interface WindowedTarget {
  frequency: `${CashflowFrequency}`
  granularity?: CashflowTargetGranularity | null
}

// Narrowest first; the window dropdown lists them in this order.
const GRANULARITIES: readonly CashflowTargetGranularity[] = [
  'monthly',
  'bimonthly',
  'quarterly',
  'semiannually',
  'annually',
]

const GRANULARITY_MONTHS: Record<CashflowTargetGranularity, number> = {
  monthly: 1,
  bimonthly: 2,
  quarterly: 3,
  semiannually: 6,
  annually: 12,
}

/** Same two rules as the backend's validation. */
export function granularityIssue(
  target: WindowedTarget,
): GranularityIssue | null {
  const granularity = target.granularity ?? null
  if (granularity === null) return null
  if (target.frequency === 'one_time') return 'granularity_on_one_time'
  // Undefined width means an unrecognized stored value (e.g. schema drift); treat it like too wide.
  const width = GRANULARITY_MONTHS[granularity]
  if (width === undefined || width > CashflowFrequencyMap[target.frequency]) {
    return 'granularity_wider_than_frequency'
  }
  return null
}

/** A stored target's window; an invalid one (the backend rejects these) reads as the whole period. */
export function parseStoredGranularity(
  target: WindowedTarget,
): CashflowTargetGranularity | null {
  const granularity = target.granularity ?? null
  return granularityIssue(target) === null ? granularity : null
}

export function windowMonths(
  frequency: RecurringFrequency,
  granularity: CashflowTargetGranularity | null,
): number {
  return granularity === null
    ? CashflowFrequencyMap[frequency]
    : GRANULARITY_MONTHS[granularity]
}

export function spacedWindowMonths(target: WindowedTarget): number | null {
  const granularity = parseStoredGranularity(target)
  if (granularity === null || target.frequency === 'one_time') return null
  const months = GRANULARITY_MONTHS[granularity]
  return months < CashflowFrequencyMap[target.frequency] ? months : null
}

/** A window as wide as the new cadence becomes the whole period, so only narrower ones survive. */
export function granularityForFrequency(
  granularity: CashflowTargetGranularity | null,
  frequency: CashflowFrequency,
): CashflowTargetGranularity | null {
  if (granularity === null || frequency === CashflowFrequency.OneTime)
    return null
  const narrower =
    GRANULARITY_MONTHS[granularity] < CashflowFrequencyMap[frequency]
  return narrower ? granularity : null
}

export function getCashflowWindowOptions(frequency: CashflowFrequency) {
  if (frequency === CashflowFrequency.OneTime) return []
  const cadence = CashflowFrequencyMap[frequency]
  return GRANULARITIES.filter(
    (granularity) => GRANULARITY_MONTHS[granularity] <= cadence,
  ).map((granularity) => {
    const months = GRANULARITY_MONTHS[granularity]
    const isWholePeriod = months === cadence
    return {
      label: `${months}-month`,
      value: granularity,
      granularity: isWholePeriod ? null : granularity,
    }
  })
}
